/**
 * What the coach page shows around an opener offer (PC-I71): the controls
 * each kind offers and the record line after an answer.
 *
 * Pure, so the rules are tested rather than judged on a screen. One dispatch
 * (`offerView`) from the offer the server sent to what this client draws, in
 * the one model, `OfferView` (#318): the stale routine, the lagging region and
 * a plateau's variation decide their words in `addOfferPresentation.ts`, a
 * plateau's rep range and deload in `adjustOfferPresentation.ts`, the injured
 * area's ask here, and all of them answer in the same four states, so one
 * component draws them all (`OfferControls`). A kind is added here or to those
 * renderers together with its declaration in `OPENER_OFFER_KINDS`
 * (offers.ts), never before. The iOS twin is GroundworkKit's
 * `OfferPresentation`, and the words both render come from the shared deck
 * (`COACH_COPY` / `CoachCopy`), which `scripts/check_copy_decks.py` keeps
 * byte-identical.
 *
 * The record line is drawn from what the server recorded (PC-I118): the offer
 * this is given has already taken its `answer` and `can_change` from the
 * route's response, never from the answer that was sent.
 */
import type { OpenerOffer } from '../../api/types'
import { COACH_COPY } from '../../copy/coach'
import { fill } from '../../copy/firstRun'
import { addOfferView, isAddOffer } from './addOfferPresentation'
import { adjustOfferView, isAdjustOffer } from './adjustOfferPresentation'

/** What an offer's controls are, as one of four states. */
export type OfferView =
  /** Nothing this client can answer: "Got it" alone dismisses the line. */
  | { kind: 'none' }
  /**
   * A recommendation waiting for an answer: one amber accept, a quiet decline
   * that records itself and folds the line away, and a scope line
   * (PC-I41, PC-I75).
   */
  | { kind: 'offer'; accept: string; decline: string; scope: string }
  /**
   * The injured area's ask waiting for an answer: one control per answer the
   * kind takes, in order and at equal weight (no answer is the recommended
   * one). The answers are the server's table (`api/coach/offers.KINDS`); the
   * labels are the deck's.
   */
  | { kind: 'ask'; controls: readonly { answer: string; label: string }[] }
  /** Answered: a quiet record line, and Undo while it can still change. */
  | { kind: 'answered'; record: string; undo: boolean }

/**
 * The six areas an injury names (#269), each with the deck's word for it.
 * An area this client has no word for reads as "injured area", never as a
 * guessed one.
 */
const AREA_WORDS: Readonly<Record<string, string>> = {
  shoulder: COACH_COPY.injuryAreaShoulder,
  elbow_wrist: COACH_COPY.injuryAreaElbowWrist,
  back: COACH_COPY.injuryAreaBack,
  hip: COACH_COPY.injuryAreaHip,
  knee: COACH_COPY.injuryAreaKnee,
  ankle: COACH_COPY.injuryAreaAnkle,
}

/** The area's word for a sentence: lowercase, from the deck. */
export function areaWord(area: unknown): string {
  return typeof area === 'string' && Object.hasOwn(AREA_WORDS, area)
    ? AREA_WORDS[area]
    : COACH_COPY.injuryAreaUnknown
}

/** The area's word for a heading or a row: the first letter capitalised. */
export function areaHeading(area: unknown): string {
  const word = areaWord(area)
  return word.charAt(0).toUpperCase() + word.slice(1)
}

/** The week-later ask (#277, PC-I68): "It's clear" or "Not yet". */
function injuryCheckView(offer: OpenerOffer): OfferView {
  const area = areaWord(offer.detail.area)
  if (offer.answer === 'clear') {
    return {
      kind: 'answered',
      record: fill(COACH_COPY.injuryClearedRecord, { area }),
      undo: offer.can_change,
    }
  }
  if (offer.answer === 'not_yet') {
    return {
      kind: 'answered',
      record: fill(COACH_COPY.injuryNotYetRecord, { area }),
      undo: offer.can_change,
    }
  }
  if (offer.answer != null || !offer.can_change) return { kind: 'none' }
  return {
    kind: 'ask',
    controls: [
      { answer: 'clear', label: COACH_COPY.injuryClear },
      { answer: 'not_yet', label: COACH_COPY.injuryNotYet },
    ],
  }
}

/** Every kind this client renders. Adding one goes with its declaration in
 *  `OPENER_OFFER_KINDS`. */
const RENDERERS: Readonly<Record<string, (offer: OpenerOffer) => OfferView>> = {
  injury_check: injuryCheckView,
}

/** What the coach page draws for *offer*: nothing for a kind it does not know. */
export function offerView(offer: OpenerOffer | null | undefined): OfferView {
  if (!offer) return { kind: 'none' }
  if (isAddOffer(offer.kind)) return addOfferView(offer)
  if (isAdjustOffer(offer.kind)) return adjustOfferView(offer)
  if (!Object.hasOwn(RENDERERS, offer.kind)) return { kind: 'none' }
  return RENDERERS[offer.kind](offer)
}

/**
 * Whether the controls are waiting for an answer: the recommendation or the
 * ask, before either has been answered.
 */
export function awaitsAnswer(view: OfferView): boolean {
  return view.kind === 'offer' || view.kind === 'ask'
}

/**
 * Whether "Got it" dismisses the line beside the offer's controls: not beside
 * the offer or the ask, whose answers are the dismissal too (a decline records
 * itself and folds the line away, PC-I75; an answer counts as asked, PC-I68),
 * and yes beside a record line and where nothing is offered.
 */
export function showsOfferGotIt(view: OfferView): boolean {
  return !awaitsAnswer(view)
}

/**
 * Whether suggestions wait for the answer (PC-I60): while a recommendation is
 * unanswered, so it is not one more suggestion among them. The injured area's
 * ask does not hold them back, as before.
 */
export function holdsBackSuggestions(offer: OpenerOffer | null | undefined): boolean {
  return offerView(offer).kind === 'offer'
}
